import { useRef, useContext, useLayoutEffect, KeyboardEvent } from "react";
import { PopoverContext } from "./PopoverContext";

export type dataStateType = "open" | "closed";

export function usePopoverContext() {
  const context = useContext(PopoverContext);
  const invokerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const arrowRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    if (!invokerRef.current) return;
    context?.setInvoker?.(invokerRef.current);
  }, [context, invokerRef]);

  useLayoutEffect(() => {
    if (!popoverRef.current) return;
    context?.setPopover?.(popoverRef.current);
    context?.setRootNode?.(popoverRef.current.getRootNode());
    if (!arrowRef.current) return;
    context?.setArrowElement?.(arrowRef.current);
  }, [context, popoverRef]);

  const onMouseEnterHandler = () => {
    if (!context?.openOnHover) return;
    context.openPopover();
  };
  const onMouseLeaveHandler = () => {
    if (!context?.openOnHover) return;
    context.closePopover();
  };

  const onFocusHandler = () => {
    if (context?.controlled || !context?.openOnHover) return;
    context?.openPopover();
  };
  const onBlurHandler = () => {
    if (
      context?.controlled ||
      context?.disableCloseOnClickOutside ||
      !context?.openOnHover
    )
      return;
    context?.closePopover();
  };

  const onKeyDownHandler = (e: KeyboardEvent) => {
    if (!context?.openState) return;
    if (
      e.code === "Escape" &&
      !context?.disableCloseOnEscape &&
      !context?.controlled
    ) {
      context?.closePopover();
      return;
    }
    const focusables = context.focusables;

    if (e.code === "Tab" && e.shiftKey && context?.modal && focusables) {
      focusables[focusables.length - 1].focus();
      e.preventDefault();
      return;
    }
  };

  const onClickHandler = () => {
    context?.invoker?.focus();
    if (context?.openOnHover) return;
    if (context?.controlled === false) {
      if (context?.openState) {
        context?.closePopover();
      } else {
        context?.openPopover();
      }
      return;
    }
  };

  if (!context || !invokerRef || !popoverRef) return;
  return {
    invoker: {
      ref: invokerRef,
      element: context.invoker,
      onMouseEnter: onMouseEnterHandler,
      onMouseLeave: onMouseLeaveHandler,
      onKeyDown: onKeyDownHandler,
      onClick: onClickHandler,
      onFocus: onFocusHandler,
      onBlur: onBlurHandler,
      "aria-haspopup": true,
      "aria-expanded": context.openState,
      "aria-details": context.popoverId,
      "data-state": context.openState ? "open" : ("closed" as dataStateType),
    },
    popover: {
      ref: popoverRef,
      element: context.popover,
      id: context.popoverId,
      onClose: context.onClose,
      show: context.openPopover,
      close: context.closePopover,
      noPadding: context.noPadding,
      style: context.popoverStyle,
      arrowRef: arrowRef,
      key: context.resetKey,
    },
    rootNode: context.rootNode,
    onClose: context.onClose,
    setOpen: context.setOpenState,
    open: context.openState,
    openOnHover: context.openOnHover,
    controlled: context.controlled,
    disableCloseOnClickOutside: context.disableCloseOnClickOutside,
    disableCloseOnEscape: context.disableCloseOnEscape,
    modal: context.modal,
    placement: context.placement,
    disableCaret: context.disableCaret,
    noPadding: context.noPadding,
    fillAvailableHeight: context.fillAvailableHeight,
    maxHeight: context.maxHeight,
    matchReferenceWidth: context.matchReferenceWidth,
    fixedWidth: context.fixedWidth,
    disableFlipFallback: context.disableFlipFallback,
    disableFocusLock: context.modal,
    disableTriggerFocus: context.disableTriggerFocus,
    focusables: context.focusables,
  };
}
